import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Stack, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { SelectElement, SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlFeatureType, GqlSettingsObservationTypeCreateInput } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

type Props = {
    onSubmit: (value: GqlSettingsObservationTypeCreateInput) => void;
    error?: ApolloError;
};

export default function ObservationTypeCreateForm({ onSubmit, error }: Props) {
    const { t } = useTranslation("observationType");

    const {
        handleSubmit,
        register,
        setError,
        control,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = useForm<GqlSettingsObservationTypeCreateInput>({
        defaultValues: {
            name: "",
            description: "",
            featureType: GqlFeatureType.LineString,
            showToClientUser: false,
        },
    });

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    const featureTypeOptions = Object.values(GqlFeatureType).map(value => ({
        id: value,
        label: t(`observationTypeCreateForm.label${value}`),
    }));

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    {...register("name")}
                    label={t("observationTypeCreateForm.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />

                <TextField
                    {...register("description")}
                    label={t("observationTypeCreateForm.labelDescription")}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    minRows={5}
                    multiline
                />
                <SelectElement
                    control={control}
                    label={t("observationTypeCreateForm.labelFeatureType")}
                    rules={{ required: true }}
                    name="featureType"
                    valueKey="id"
                    options={featureTypeOptions}
                />
                <SwitchElement
                    control={control}
                    name="showToClientUser"
                    label={t("observationTypeCreateForm.labelShowToClientUser")}
                />

                <GaiaHookFormSubmitButton control={control} startIcon={<Save />}>
                    {t("observationTypeCreateForm.btnSubmit")}
                </GaiaHookFormSubmitButton>
            </Stack>
        </form>
    );
}

export type ObservationTypeCreateFormInputs = {
    name: string;
    description: string;
    featureType: GqlFeatureType;
};
